import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress,
} from '@material-ui/core';

// UTILITY
import { trimCharacters } from '@/utility/common';

// COMPONENTS
import RadioCard from '@/components/Form/RadioCard';

// STYLES
import { TitleContainer, Title, DividerStyled, LoaderContainer } from '../styles';

const ZipCode = props => {
  const {
    t,
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
    counties,
    onRadioCardChange,
    onSubmitPress,
    loading,
  } = props;
  const showCounties = (counties && counties.length !== 1) || !counties;

  useEffect(() => {
    if (values.zipcode) {
      setFieldTouched('zipcode', true, true);
    }
  }, []);

  const handleCountyChange = event => {
    onRadioCardChange(event, handleChange);
  };

  const handleChangeZipCode = event => {
    setFieldValue('zipcode', trimCharacters(event.target.value));
  };

  return (
    <div>
      <TitleContainer className="zipcode-title">
        <Title variant="h4" align="center" component="h1">
          {t('start.zipcode.title')}
        </Title>
        <Typography variant="subtitle2" align="center">
          {t('start.zipcode.subtitle')}
        </Typography>
      </TitleContainer>
      <Grid container spacing={4}>
        <Grid item xs={7} md={8} lg={9}>
          <TextField
            id="zipcode"
            className="zipcodeinput"
            label={t('inputs.zipcode')}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  className={Boolean(!errors.zipcode) && !!values.zipcode ? 'success' : ''}
                  position="start"
                >
                  <i className="icon-check" />
                </InputAdornment>
              ),
            }}
            type="tel"
            name="zipcode"
            value={values.zipcode}
            onChange={handleChangeZipCode}
            onBlur={handleBlur}
            error={touched.zipcode && !!errors.zipcode}
            helperText={touched.zipcode && errors.zipcode}
          />
        </Grid>
        <Grid item xs={5} md={4} lg={3}>
          <Button
            color="primary"
            fullWidth
            onClick={() => onSubmitPress(values.zipcode)}
            disabled={!values.zipcode.length || !!errors.zipcode}
            style={{ marginTop: '5px' }}
            className="zipcode__submit"
          >
            {t('buttons.submit')}
          </Button>
        </Grid>
      </Grid>
      {showCounties && <DividerStyled />}
      <TitleContainer>
        {showCounties && (
          <Title variant="h4" align="center" component="h1">
            {t('start.countiesCSR.title')}
          </Title>
        )}
        {!counties && !loading && (
          <Typography variant="subtitle2" align="center">
            {t('start.countiesCSR.subtitle')}
          </Typography>
        )}
        {counties && counties.length === 0 && !loading ? (
          <Typography variant="subtitle2" align="center">
            {t('start.countiesCSR.noCountyNeeded')}
          </Typography>
        ) : null}
      </TitleContainer>
      {!!counties && counties.length > 1 && (
        <Grid container spacing={1} justifyContent="center">
          {counties.map(item => (
            <Grid item xs={6} sm={6} md={3} key={item.county_id}>
              <RadioCard
                name="county_id_fk"
                text={item.county_name}
                handleChange={handleCountyChange}
                handleBlur={handleBlur}
                value={item.county_id.toString()}
                checkedValue={values.county_id_fk}
                dataAttributes={{ 'data-dma_id_fk': item.dma_id_fk.toString() }}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {loading && (
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      )}
    </div>
  );
};

ZipCode.defaultProps = {
  onSubmitPress: () => {},
  loading: false,
};

ZipCode.propTypes = {
  t: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  counties: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  onRadioCardChange: PropTypes.func.isRequired,
  onSubmitPress: PropTypes.func,
  loading: PropTypes.bool,
};

export default withTranslation()(React.memo(ZipCode));
